import { ReactNode, useEffect, useRef, useState } from 'react';

export type UnsavedChangesBarVariant = 'embedded' | 'standalone';

interface UnsavedChangesBarProps {
    dirty?: boolean;
    /** `embedded` (default): square at rest. `standalone`: fully rounded at rest + 20px bottom margin. Both use bottom radius when stuck. */
    variant?: UnsavedChangesBarVariant;
    className?: string;
    children: ReactNode;
}

/**
 * Sticky save/discard bar. When stuck to the top of the scroll container,
 * applies `--border-radius-lg-bottom` via `.is-stuck`.
 */
export const UnsavedChangesBar = ({
    dirty = false,
    variant = 'embedded',
    className = '',
    children,
}: UnsavedChangesBarProps) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [isStuck, setIsStuck] = useState(false);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        const bar = barRef.current;
        if (!sentinel || !bar) return;

        let observer: IntersectionObserver | null = null;

        const observe = () => {
            observer?.disconnect();
            const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
            observer = new IntersectionObserver(
                ([entry]) => {
                    setIsStuck(!entry.isIntersecting);
                },
                {
                    threshold: 0,
                    rootMargin: `-${stickyTop}px 0px 0px 0px`,
                },
            );
            observer.observe(sentinel);
        };

        observe();
        window.addEventListener('resize', observe);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', observe);
        };
    }, []);

    const classes = [
        'unsaved-changes-bar',
        variant === 'standalone' ? 'unsaved-changes-bar--standalone' : '',
        dirty ? 'dirty' : '',
        isStuck ? 'is-stuck' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            <div ref={sentinelRef} className="unsaved-changes-bar-sentinel" aria-hidden="true" />
            <div ref={barRef} className={classes}>
                {children}
            </div>
        </>
    );
};
